import React from 'react';
import PropTypes from 'prop-types';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Box, IconButton, Tooltip, Typography } from '@material-ui/core';
import { Avatar, Button, Chip } from '@orijinworks/frontend-commons';
import { COLORS } from '../constants';
import { getPersonDisplayName } from '../persons-manager.helpers';
import { usePersonCardStyles } from './person-card.style';

const AVATAR_SIZE = 40;

const personPropType = PropTypes.shape({
  personId: PropTypes.number.isRequired,
  displayFirstName: PropTypes.string,
  firstName: PropTypes.string,
  displayLastName: PropTypes.string,
  lastName: PropTypes.string,
  username: PropTypes.string,
  docId: PropTypes.string,
  email: PropTypes.string,
});

const attributeRowPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  attributeName: PropTypes.string,
  value: PropTypes.string,
});

const attributesStatePropType = PropTypes.shape({
  status: PropTypes.oneOf(['loading', 'idle', 'ok', 'error']),
  items: PropTypes.arrayOf(attributeRowPropType),
});

const servicesStatePropType = PropTypes.shape({
  status: PropTypes.oneOf(['loading', 'idle', 'ok', 'error']),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      displayName: PropTypes.string,
      serviceName: PropTypes.string,
    }),
  ),
});

const profileHrefForPerson = (personId) => `/#/users/${personId}`;

const renderPreviewChips = (rows, max, getLabel, classes) => {
  const shown = rows.slice(0, max);
  const hiddenCount = Math.max(0, rows.length - shown.length);

  return (
    <>
      {shown.map((row) => (
        <Chip
          key={row.id}
          className={classes.attributeChip}
          classes={{ label: classes.attributeChipLabel }}
          size="small"
          variant="outlined"
          color="primary"
          label={getLabel(row)}
        />
      ))}
      {hiddenCount > 0 && <Chip size="small" label={`+${hiddenCount} more`} variant="outlined" />}
    </>
  );
};

const PersonCardInner = ({ person, attributesState, onOpenDetail, servicesState }) => {
  const classes = usePersonCardStyles();
  /** @type {string} */
  const name = getPersonDisplayName(person);
  const first = person.displayFirstName || person.firstName || '';
  const last = person.displayLastName || person.lastName || '';

  const { status, items } = attributesState || { status: 'loading', items: [] };
  const { status: servicesStatus, items: servicesItems } = servicesState || { status: 'loading', items: [] };

  return (
    <Box className={classes.root}>
      <Box className={classes.topRow}>
        <Avatar
          renderState="initials"
          firstName={first || name}
          lastName={last || '.'}
          iconSize={AVATAR_SIZE}
          decorative
          avatarProps={{
            backgroundColor: COLORS.primary,
            textColor: '#fff',
          }}
        />
        <Box className={classes.identity}>
          <Typography className={classes.name} component="div">
            {name}
          </Typography>
          <Typography className={classes.meta} component="div">
            {person.username && (
              <Typography component="span" display="inline" className={classes.meta}>
                @{person.username}
              </Typography>
            )}
            {person.docId && (
              <Typography component="span" display="inline" className={classes.meta}>
                {person.username ? ' · ' : ''}
                Doc ID: {person.docId}
              </Typography>
            )}
          </Typography>
          {person.email && (
            <Typography className={classes.meta} component="div">
              {person.email}
            </Typography>
          )}
        </Box>
        <Box className={classes.editBtn}>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={() => onOpenDetail(person.personId)}
          >
            View details
          </Button>
          <Tooltip title="Open user profile in new tab">
            <IconButton
              component="a"
              href={profileHrefForPerson(person.personId)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open user profile in new tab"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box className={classes.section}>
        <Typography variant="caption" className={classes.sectionTitle}>
          Attributes
        </Typography>
        <Box className={classes.chips}>
          {(status === 'loading' || status === 'idle') && (
            <Typography variant="caption" className={classes.meta}>
              Loading attributes…
            </Typography>
          )}
          {status === 'error' && (
            <Typography variant="caption" color="error">
              Could not load attributes
            </Typography>
          )}
          {status === 'ok' && items.length === 0 && (
            <Typography variant="caption" className={classes.meta}>
              No person attributes
            </Typography>
          )}
          {status === 'ok' &&
            renderPreviewChips(
              items,
              3,
              (row) => `${row.attributeName}: ${row.value || ''}`,
              classes,
            )}
        </Box>
      </Box>
      <Box className={classes.section}>
        <Typography variant="caption" className={classes.sectionTitle}>
          Services
        </Typography>
        <Box className={classes.chips}>
          {(servicesStatus === 'loading' || servicesStatus === 'idle') && (
            <Typography variant="caption" className={classes.meta}>
              Loading services…
            </Typography>
          )}
          {servicesStatus === 'error' && (
            <Typography variant="caption" color="error">
              Could not load services
            </Typography>
          )}
          {servicesStatus === 'ok' && servicesItems.length === 0 && (
            <Typography variant="caption" className={classes.meta}>
              No person services
            </Typography>
          )}
          {servicesStatus === 'ok' &&
            renderPreviewChips(
              servicesItems,
              3,
              (row) => row.displayName || row.serviceName || 'Service',
              classes,
            )}
        </Box>
      </Box>
    </Box>
  );
};

PersonCardInner.propTypes = {
  person: personPropType.isRequired,
  attributesState: attributesStatePropType,
  onOpenDetail: PropTypes.func.isRequired,
  servicesState: servicesStatePropType,
};

PersonCardInner.defaultProps = {
  attributesState: undefined,
  servicesState: undefined,
};

export const PersonCard = React.memo(PersonCardInner);
PersonCard.displayName = 'PersonCard';
